import React from "react"
import { twMerge } from "tailwind-merge"

import { getSectionColorScheme } from "../utils/colorModeUtils"
import { getContrastSafeButtonTextColor } from "../utils/colorUtils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"
import { shouldShowReviewHeroRatingSummary } from "../utils/reviewHeroUtils"
import { applyButtonTheme, getButtonClasses, getThemeShapeClasses } from "../utils/themeUtils"
import { buildPublicPageUrl, buildServiceUrl, buildBookingUrl } from "../utils/urlUtils"

const getTotalReviewsCount = (practice, reviews = []) => {
  if (practice?.reviewsCount !== undefined && practice.reviewsCount !== null && !Number.isNaN(practice.reviewsCount)) {
    return Number(practice.reviewsCount)
  }
  if (
    practice?.reviews_count !== undefined &&
    practice.reviews_count !== null &&
    !Number.isNaN(practice.reviews_count)
  ) {
    return Number(practice.reviews_count)
  }
  return reviews.length
}

const getAverageRating = (practice, reviews = []) => {
  if (practice?.reviewsAverage !== undefined && practice.reviewsAverage !== null) {
    return Number.parseFloat(practice.reviewsAverage).toFixed(1)
  }
  if (practice?.reviews_average !== undefined && practice.reviews_average !== null) {
    return Number.parseFloat(practice.reviews_average).toFixed(1)
  }

  if (reviews.length === 0) return "0.0"

  const sum = reviews.reduce((total, review) => {
    const rating = review.rating || review.score || review.stars || review.ratingValue || 0
    return total + (rating || 0)
  }, 0)

  return (sum / reviews.length).toFixed(1)
}

const handleReviewHeroCTAClick = (settings, practice) => {
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""

  const scrollToLayout = (layoutId) => {
    if (!layoutId) return
    const targetElement = document.querySelector(`[id^=section-${layoutId}-]`)
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" })
    }
  }

  switch (ctaAction) {
    case "page":
      if (ctaTarget) window.location.href = buildPublicPageUrl(ctaTarget, practice)
      break
    case "service":
      window.location.href = ctaTarget ? buildServiceUrl(ctaTarget, practice) : buildBookingUrl(practice)
      break
    case "booking":
      window.location.href = buildBookingUrl(practice)
      break
    case "contact":
      scrollToLayout("contact")
      break
    case "scroll":
      window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
      break
    case "external":
      if (ctaTarget) {
        const url = ctaTarget.startsWith("http") ? ctaTarget : `https://${ctaTarget}`
        window.open(url, "_blank")
      }
      break
    default:
      break
  }
}

const ReviewHeroContent = ({
  section,
  practice,
  theme,
  textColor,
  hasBackgroundImage = false,
  isMobile = false,
  align = "center"
}) => {
  const settings = section?.settings || {}
  const colorScheme = getSectionColorScheme(settings, theme)
  const headingColor = hasBackgroundImage ? textColor : settings.headlineColor || colorScheme.headingColor || textColor
  const bodyColor = hasBackgroundImage ? textColor : settings.subheadlineColor || colorScheme.bodyColor || textColor
  const buttonStyles = colorScheme.primaryButton
  const ctaBackgroundColor = hasBackgroundImage ? theme?.colors?.primary || "#BD654F" : buttonStyles.backgroundColor
  const ctaTextColor = hasBackgroundImage
    ? getContrastSafeButtonTextColor(ctaBackgroundColor, theme)
    : buttonStyles.color
  const ctaHoverBackgroundColor = hasBackgroundImage
    ? theme?.colors?.primaryDark || "#A55A45"
    : buttonStyles.hoverBackgroundColor
  const paragraphFont = formatFontFamily(theme?.fonts?.paragraph)
  const headingFont = formatHeadingFontFamily(theme?.fonts?.heading)
  const reviews = practice?.reviews || []
  const totalReviewsCount = getTotalReviewsCount(practice, reviews)
  const averageRating = getAverageRating(practice, reviews)
  const hasReviews = totalReviewsCount > 0
  const titleText = section?.headline || "What clients are saying"
  const showRatingSummary = shouldShowReviewHeroRatingSummary(settings)
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true
  const ctaText = settings.ctaText || "Book a Discovery Call"

  const headlineClass = isMobile
    ? "text-[32px] font-bold leading-[40px]"
    : "text-[40px] font-bold leading-[48px] md:text-[56px] md:leading-[64px]"
  const starSizeClass = isMobile ? "size-5" : "size-7"
  const logoSizeClass = isMobile ? "size-8" : "size-10"
  const badgeSizeClass = isMobile ? "size-4" : "size-5"
  const checkSizeClass = isMobile ? "size-2.5" : "size-3"
  const verifiedLinkClass = isMobile
    ? "text-[11px] font-semibold uppercase tracking-wider hover:underline"
    : "text-sm font-semibold uppercase tracking-wider hover:underline"
  const ratingLabelClass = isMobile
    ? "text-[12px] font-semibold uppercase tracking-wider"
    : "text-sm font-semibold uppercase tracking-wider"
  const reviewCountClass = isMobile ? "text-[12px] font-medium" : "text-sm font-medium"
  const contentGapClass = isMobile ? "gap-5" : "gap-6"
  const ratingGapClass = isMobile ? "gap-2" : "gap-3"
  const verifiedGapClass = isMobile ? "gap-3" : "gap-4"

  const isStartAligned = align === "start"

  return (
    <div
      className={twMerge(
        "flex flex-col",
        isStartAligned ? "items-start text-left" : "items-center text-center",
        contentGapClass
      )}>
      <div
        className={twMerge("flex items-center", verifiedGapClass, isStartAligned ? "justify-start" : "justify-center")}>
        <div className={twMerge("relative", logoSizeClass)}>
          <img src="/images/HealmeLogo.png" alt="Heal.me" className="size-full object-contain" />
          <div
            className={twMerge(
              "absolute -bottom-1 -right-1 flex items-center justify-center rounded-full shadow-sm",
              badgeSizeClass,
              getThemeShapeClasses(theme)
            )}
            style={{ backgroundColor: "#0D9DA4" }}>
            <img src="/images/icons/verified-checkmark.svg" alt="verified" className={checkSizeClass} />
          </div>
        </div>
        <div className={twMerge("flex items-center gap-1 px-0 py-1", getThemeShapeClasses(theme))}>
          <a
            href={typeof window !== "undefined" ? window.location.origin : "/"}
            target="_blank"
            rel="noopener noreferrer"
            className={verifiedLinkClass}
            style={{
              fontFamily: paragraphFont,
              color: bodyColor
            }}>
            Heal.me verified Reviews
          </a>
        </div>
      </div>

      <h1
        className={twMerge("break-words", headlineClass)}
        style={{
          fontFamily: headingFont,
          fontWeight: theme?.fonts?.headingWeight || "700",
          color: headingColor,
          wordBreak: "normal",
          overflowWrap: "break-word"
        }}>
        {titleText}
      </h1>

      {hasReviews && showRatingSummary && (
        <div
          className={twMerge(
            "flex flex-col",
            isStartAligned ? "items-start text-left" : "items-center text-center",
            ratingGapClass
          )}>
          <div className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <img
                key={i}
                src={
                  i < parseFloat(averageRating)
                    ? "/images/email_mailer/star--5.png"
                    : "/images/email_mailer/star--1.png"
                }
                alt={i < parseFloat(averageRating) ? "filled star" : "empty star"}
                className={starSizeClass}
              />
            ))}
          </div>
          <div
            className={twMerge(
              "flex flex-col",
              isStartAligned ? "items-start" : "items-center",
              isMobile ? "gap-0.5" : "gap-1"
            )}>
            <span
              className={ratingLabelClass}
              style={{
                fontFamily: paragraphFont,
                color: bodyColor
              }}>
              {averageRating} average rating
            </span>
            <span
              className={reviewCountClass}
              style={{
                fontFamily: paragraphFont,
                color: bodyColor
              }}>
              {totalReviewsCount} Review{totalReviewsCount !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}

      {showCTA && (
        <button
          type="button"
          aria-label={ctaText || "Call to action"}
          onClick={() => handleReviewHeroCTAClick(settings, practice)}
          className={twMerge(
            "inline-flex items-center justify-center px-10 py-3 text-[16px] font-normal leading-[24px] transition-all hover:shadow-lg",
            getButtonClasses(theme?.buttonStyle)
          )}
          style={applyButtonTheme(
            {
              backgroundColor: ctaBackgroundColor,
              color: ctaTextColor,
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
            },
            theme
          )}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = ctaHoverBackgroundColor
            if (!hasBackgroundImage) {
              e.target.style.color = ctaTextColor
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = ctaBackgroundColor
            e.target.style.color = ctaTextColor
          }}>
          {ctaText}
        </button>
      )}
    </div>
  )
}

export default ReviewHeroContent
